import { useEffect, useMemo, useState, useCallback } from "react";
import {
    Box,
    Stack,
    Typography,
    IconButton,
    TextField,
    Checkbox,
    Button,
    CircularProgress,
    Alert,
    Divider,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import { useNavigate, useParams } from "react-router-dom";
import { listEligibleEmployees, saveSelectedEmployees } from "../api/liquidacionApi";
import { getSucursales } from "../../empleados/api/empleadosApi";
import { mapApiError } from "../../../../shared/utils/mapApiError";
import { unwrapHttp } from "../../../../shared/utils/unwrapHttp";
import CardShell from '../components/CardShell';
import { HeaderPill, ColHeader } from './ClosurePayrollDataPage'; // Reutilizamos estilos

function normalizeBranchName(name) {
    return (name || "Sin sucursal").trim() || "Sin sucursal";
}

function toBranchOption(b) {
    const id = b?.id ?? b?.branchId ?? b?.branch_id;
    const name = b?.name ?? b?.nombre ?? b?.branchName;
    if (!id) return null;
    return { id: Number(id), name: normalizeBranchName(name) };
}

export default function ClosureUsersSelectionPage() {
    const navigate = useNavigate();
    const { closureId } = useParams();

    const [searchName, setSearchName] = useState("");
    const [branchId, setBranchId] = useState(null);
    const [branches, setBranches] = useState([]);
    const [branchesLoading, setBranchesLoading] = useState(false);

    const [items, setItems] = useState([]);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [page, setPage] = useState(1);
    const [limit] = useState(50);
    const [savingSelection, setSavingSelection] = useState(false);
    const [saveError, setSaveError] = useState("");

    const [meta, setMeta] = useState({
        page: 1, limit: 50, totalItems: 0, totalPages: 0, hasNextPage: false, hasPreviousPage: false,
    });

    const selectedCount = selectedIds.size;
    const tableCols = "60px minmax(280px, 2.5fr) 180px minmax(220px, 1.5fr) minmax(220px, 1.5fr)";

    const filterInputSx = {
        "& .MuiOutlinedInput-root": {
            bgcolor: "#F3F4F6", borderRadius: "999px", height: 46, px: 1,
            border: "1px solid rgba(15,23,42,0.18)", boxShadow: "0 2px 0 rgba(0,0,0,0.06)",
            "& fieldset": { border: "none" },
            "&:hover": { borderColor: "rgba(15,23,42,0.30)" },
            "&.Mui-focused": { borderColor: "rgba(15,23,42,0.40)" },
        },
        "& .MuiOutlinedInput-input": { py: 1.2, fontWeight: 700, color: "#0F172A", fontSize: 14 },
        "& input::placeholder": { opacity: 0.9, fontWeight: 700, color: "#0F172A" },
    };

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                setBranchesLoading(true);
                const resp = await getSucursales();
                const data = unwrapHttp(resp);
                const list = Array.isArray(data) ? data : data?.data || [];
                const opts = list.map(toBranchOption).filter(Boolean);
                if (!mounted) return;
                setBranches(opts.sort((a, b) => a.name.localeCompare(b.name)));
            } catch {
                if (!mounted) return;
                setBranches([]);
            } finally {
                if (!mounted) return;
                setBranchesLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, []);

    const fetchData = useCallback(async (opts = {}) => {
        if (!closureId) return;
        setLoading(true);
        setError("");
        try {
            const effectiveBranchId = opts.branchId !== undefined ? opts.branchId : branchId;
            const effectiveSearchName = opts.searchName !== undefined ? opts.searchName : searchName;
            const effectivePage = opts.page !== undefined ? opts.page : page;

            const res = await listEligibleEmployees(closureId, {
                searchName: effectiveSearchName || undefined,
                branchId: effectiveBranchId == null ? undefined : Number(effectiveBranchId),
                page: effectivePage,
                limit,
            });
            const data = unwrapHttp(res);
            setItems(Array.isArray(data) ? data : data?.data || []);

            // Usamos un callback aquí para no depender del estado 'meta' y evitar renders extra
            setMeta((prevMeta) => data?.meta || prevMeta);
        } catch (err) {
            setItems([]);
            setError(mapApiError(err).message);
        } finally {
            setLoading(false);
        }
    }, [closureId, branchId, searchName, page, limit]);

    useEffect(() => {
        fetchData();
    }, [closureId, page, fetchData])

    useEffect(() => {
        const t = setTimeout(() => {
            setPage(1);
            fetchData({ page: 1, searchName });
        }, 350);
        return () => clearTimeout(t);
    }, [searchName, fetchData]);
    
    useEffect(() => {
        setPage(1);
        fetchData({ page: 1, branchId });
    }, [branchId, fetchData]);

    const toggleId = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const allVisibleSelected = useMemo(() => items.length > 0 && items.every((x) => selectedIds.has(x.id)), [items, selectedIds]);

    const toggleAllVisible = () => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (allVisibleSelected) items.forEach((x) => next.delete(x.id));
            else items.forEach((x) => next.add(x.id));
            return next;
        });
    };

    const handleContinue = async () => {
        if (selectedIds.size === 0) return setSaveError("Debe seleccionar al menos un empleado");
        setSavingSelection(true);
        setSaveError("");
        try {
            await saveSelectedEmployees(closureId, Array.from(selectedIds));
            navigate(`/rrhh/liquidacion/${closureId}/datos`);
        } catch (err) {
            setSaveError(mapApiError(err).message || "Error al guardar la selección");
        } finally {
            setSavingSelection(false);
        }
    };

    const handlePrevPage = () => {

        if (loading) return;

        if (meta?.hasPreviousPage) setPage((p) => Math.max(1, p - 1));

    };


    const handleNextPage = () => {

        if (loading) return;

        if (meta?.hasNextPage) setPage((p) => p + 1);

    };

    return (
        <Box sx={{ p: 2, height: "calc(100vh - 64px)", display: "flex", flexDirection: "column", gap: 2, overflow: "hidden" }}>

            {/* Header */}
            <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
                <IconButton onClick={() => navigate("/rrhh/liquidacion/")} sx={{ width: 40, height: 40, bgcolor: "secondary.500", "&:hover": { bgcolor: "secondary.600" } }}>
                    <ArrowBackIcon sx={{ color: "logoblanco", fontSize: 32 }} />
                </IconButton>

                <HeaderPill sx={{ bgcolor: 'secondary.200' }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'primary.900' }}>
                        Selección de Usuarios a Cerrar
                    </Typography>
                </HeaderPill>
                <Box sx={{ width: 40 }} /> {/* Spacer */}
            </Stack>

            {saveError && <Alert severity="error">{saveError}</Alert>}
            {error && <Alert severity="error">{error}</Alert>}

            {/* Contenedor Gris Maximizado */}
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", bgcolor: "#A9A9B6", borderRadius: "24px", p: 2, overflow: "hidden" }}>

                {/* Filtros */}
                <Stack direction={{ xs: "column", md: "row" }} sx={{ gap: 2, width: "100%", mb: 2, flexShrink: 0 }}>
                    <Box sx={{ flex: 1 }}>
                        <TextField fullWidth value={searchName} onChange={(e) => setSearchName(e.target.value)} placeholder="Buscar por nombre..." size="small" sx={filterInputSx} />
                    </Box>
                    <Box sx={{ width: { xs: "100%", md: 400 } }}>
                        <Autocomplete
                            options={branches}
                            value={branchId != null ? branches.find((b) => Number(b.id) === Number(branchId)) || null : null}
                            loading={branchesLoading}
                            onChange={(_, next) => setBranchId(next?.id ?? null)}
                            getOptionLabel={(opt) => opt?.name || ""}
                            isOptionEqualToValue={(opt, val) => Number(opt?.id) === Number(val?.id)}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Sucursal: Todas" size="small" sx={filterInputSx}
                                    InputProps={{ ...params.InputProps, endAdornment: (<>{branchesLoading ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</>) }}
                                />
                            )}
                        />
                    </Box>
                </Stack>

                {/* Tabla en CardShell unificado */}
                <CardShell sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", p: 2 }}>

                    {/* Header Tabla Fijo */}
                    <Box sx={{ display: "grid", gridTemplateColumns: tableCols, gap: 1.5, px: 1.5, pb: 1.5, alignItems: "center", flexShrink: 0 }}>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Checkbox
                                checked={allVisibleSelected && items.length > 0}
                                indeterminate={!allVisibleSelected && selectedCount > 0}
                                onChange={toggleAllVisible}
                                sx={{ '& .MuiSvgIcon-root': { fontSize: 26 } }}
                            />
                        </Box>
                        <ColHeader>Nombre y Apellido</ColHeader>
                        <ColHeader>DNI</ColHeader>
                        <ColHeader>Sucursal</ColHeader>
                        <ColHeader>Área</ColHeader>
                    </Box>

                    {/* Scrollable Body */}
                    <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 1 }}>
                        {loading ? (
                            <Stack sx={{ alignItems: "center", py: 6 }}><CircularProgress /></Stack>
                        ) : items.length === 0 ? (
                            <Typography sx={{ textAlign: "center", py: 4, fontWeight: 700, fontSize: 15, color: "neutral.700" }}>
                                No se encontraron empleados elegibles.
                            </Typography>
                        ) : (
                            <Stack sx={{ gap: 1 }}>
                                {items.map((row) => {
                                    const checked = selectedIds.has(row.id);
                                    return (
                                        <Box
                                            key={row.id} onClick={() => toggleId(row.id)}
                                            sx={{
                                                display: "grid", gridTemplateColumns: tableCols, alignItems: "center",
                                                gap: 1.5, px: 1.5, py: 1.2, cursor: "pointer",
                                                bgcolor: checked ? "rgba(59, 130, 246, 0.08)" : "neutral.50",
                                                borderRadius: "16px", border: "1px solid",
                                                borderColor: checked ? "primary.300" : "neutral.200",
                                                transition: "all 0.15s ease",
                                                "&:hover": { bgcolor: checked ? "rgba(59, 130, 246, 0.15)" : "neutral.100" }
                                            }}
                                        >
                                            <Box sx={{ display: "flex", justifyContent: "center" }} onClick={(e) => e.stopPropagation()}>
                                                <Checkbox checked={checked} onChange={() => toggleId(row.id)} sx={{ '& .MuiSvgIcon-root': { fontSize: 24 } }} />
                                            </Box>
                                            <Typography sx={{ fontWeight: 700, fontSize: 14, color: "primary.900", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.fullName || "—"}</Typography>
                                            <Typography sx={{ fontWeight: 700, fontSize: 14, color: "primary.900" }}>{row.dni || "—"}</Typography>
                                            <Typography sx={{ fontWeight: 700, fontSize: 14, color: "primary.900", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.branchName || row.branch?.name || "—"}</Typography>
                                            <Typography sx={{ fontWeight: 700, fontSize: 14, color: "primary.900", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.areaName || row.area?.name || "—"}</Typography>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        )}
                    </Box>

                    {/* Paginación fijada en el pie de la tabla */}
                    <Box sx={{ pt: 2, flexShrink: 0 }}>
                        <Divider sx={{ mb: 1, borderColor: "neutral.400" }} />
                        <Stack direction="row" sx={{ alignItems: "center", justifyContent: "flex-end", gap: 2 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: 14, color: "primary.900" }}>
                                Página {meta?.page || page} de {meta?.totalPages || 1}
                            </Typography>
                            <Stack direction="row" sx={{ gap: 1 }}>
                                <Button onClick={handlePrevPage} disabled={loading || !meta?.hasPreviousPage} sx={{ minWidth: 40, color: "primary.900", bgcolor: "neutral.200", '&:hover': { bgcolor: "neutral.400" } }}><ArrowLeftIcon /></Button>
                                <Button onClick={handleNextPage} disabled={loading || !meta?.hasNextPage} sx={{ minWidth: 40, color: "primary.900", bgcolor: "neutral.200", '&:hover': { bgcolor: "neutral.400" } }}><ArrowRightIcon /></Button>
                            </Stack>
                        </Stack>
                    </Box>
                </CardShell>
            </Box>

            {/* Botones de acción inferiores */}
            <Stack direction="row" sx={{ justifyContent: "flex-end", gap: 2, flexShrink: 0 }}>
                <Button onClick={handleContinue} disabled={selectedIds.size === 0 || savingSelection} sx={{ bgcolor: "#b9f6ca", color: "primary.900", px: 3, py: 1, borderRadius: "24px", border: "2px solid", textTransform: "none", fontWeight: 800, fontSize: 16, display: "flex", gap: 1, alignItems: "center", "&:hover": { bgcolor: "#a5f5bd" }, "&:disabled": { opacity: 0.6 } }}>
                    {savingSelection ? <CircularProgress size={20} color="inherit" /> : "Continuar"}
                </Button>
            </Stack>
        </Box>
    );
}